/**
 * The portal's UI state, and its one connection to the server.
 *
 * THE DIVIDING LINE THIS FILE DEFENDS: the reducer holds only what the *interface* is doing —
 * which screen is showing, which dog is selected, what has been typed into the search box, whether
 * a toast is up. It holds no record data at all. The record comes from `useMe()`, which is the
 * single copy, loaded once.
 *
 * Duplicating server data into a reducer is the bug that ends with Home and Reminders disagreeing
 * about when a treatment is due. The care schedule and the food plan are derived on the SERVER for
 * the same reason; the client formats them and never recomputes them.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type ReactElement,
  type ReactNode,
} from 'react'
import type {
  CareItem,
  CareItemKind,
  DogPayload,
  FoodAvailability,
  MarkGivenRequest,
  MarkGivenResponse,
  MePayload,
} from '@ft/shared'

import { getMe, markGiven as postMarkGiven, type ApiFailure, type ApiResult } from '../api/client'
import type { ScreenId } from '../model/nav'

export type { ScreenId }

// ── Motion ──────────────────────────────────────────────────────────────────────────────────────

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

function motionQuery(): MediaQueryList | null {
  // jsdom and any non-browser render have no matchMedia. Treat its absence as "no preference
  // expressed", which is what a fresh browser reports too.
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  return window.matchMedia(REDUCED_MOTION)
}

function subscribeToMotion(onChange: () => void): () => void {
  const query = motionQuery()
  if (!query) return () => undefined
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

function motionSnapshot(): boolean {
  return motionQuery()?.matches ?? false
}

/**
 * Reads the OS "reduce motion" setting, live.
 *
 * Use it for anything JavaScript animates. Anything CSS animates is already handled by the
 * `@media (prefers-reduced-motion:reduce)` block in portal.css.
 */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribeToMotion, motionSnapshot, () => false)
}

/**
 * Scroll the page back to the top after a navigation.
 *
 * The preference is re-read here, at call time, rather than taken from the hook, because CSS
 * `scroll-behavior` cannot reach a scripted `window.scrollTo` — a reduced-motion user would still
 * get a smooth scripted scroll. This is called on screen changes and on wizard jumps; deliberately
 * NOT on stepping back inside the wizard, where the page is already where the reader left it.
 */
export function scrollTop(): void {
  if (typeof window === 'undefined') return
  window.scrollTo({ top: 0, behavior: motionSnapshot() ? 'auto' : 'smooth' })
}

// ── UI state ────────────────────────────────────────────────────────────────────────────────────

/** Profile section filter. `all` is the default and shows every section. */
export type TabId = 'all' | 'identity' | 'medical' | 'care' | 'owner' | 'documents'

/**
 * The six review steps.
 *
 * A literal union rather than `number`, on purpose. The prototype incremented a plain number
 * against a render-captured guard, so a double-click could push it to 7 — where the title, the
 * question and the help text were all `undefined`, the header read "Step 7 of 6", and every
 * progress bar painted as complete. `nextStep()` below cannot produce a step that does not exist.
 */
export type StepId = 0 | 1 | 2 | 3 | 4 | 5

export const STEP_IDS: readonly StepId[] = [0, 1, 2, 3, 4, 5]
export const STEP_COUNT = STEP_IDS.length

export interface ToastState {
  /**
   * Cleared 350ms AFTER the fade finishes, not when it starts. The toast element is never
   * unmounted — it only fades — so a `role="status"` region that keeps its text goes on holding
   * stale news for a screen reader long after it has visually gone.
   */
  readonly message: string
  readonly visible: boolean
}

/** Reminder switches are keyed by `CareItemKind`, so `toggles[item.kind]` is always defined. */
export type ToggleKey = CareItemKind

/**
 * In the prototype these were keyed by a hand-written list that had drifted from the care
 * definitions, and a miss produced `aria-checked="undefined"` — an invalid ARIA value, not
 * "false". One union for both removes the failure mode entirely.
 */
export const INITIAL_TOGGLES: Readonly<Record<ToggleKey, boolean>> = {
  worming: true,
  flea: true,
  booster: true,
  kennel: true,
  contact: true,
  // Feeding is a daily routine, not a due date — nudging about it every day would be noise.
  feeding: false,
}

export interface PortalUiState {
  readonly screen: ScreenId
  /** `null` means "whichever dog is first" — see `useSelectedDog`. 4.9% of households have two. */
  readonly selectedDogId: string | null
  readonly tab: TabId
  /** Stored exactly as typed. Every consumer trims; the input must not fight the typist. */
  readonly search: string
  readonly step: StepId
  readonly toast: ToastState
  readonly toggles: Readonly<Record<ToggleKey, boolean>>
}

export const INITIAL_UI_STATE: PortalUiState = {
  screen: 'home',
  selectedDogId: null,
  tab: 'all',
  search: '',
  step: 0,
  toast: { message: '', visible: false },
  toggles: INITIAL_TOGGLES,
}

export type PortalAction =
  | { readonly type: 'screen/set'; readonly screen: ScreenId }
  | { readonly type: 'dog/select'; readonly dogId: string }
  | { readonly type: 'tab/set'; readonly tab: TabId }
  | { readonly type: 'search/set'; readonly search: string }
  | { readonly type: 'step/set'; readonly step: StepId }
  | { readonly type: 'reminder/toggle'; readonly key: ToggleKey }
  | { readonly type: 'toast/show'; readonly message: string }
  | { readonly type: 'toast/hide' }
  | { readonly type: 'toast/clear' }

export function portalReducer(state: PortalUiState, action: PortalAction): PortalUiState {
  switch (action.type) {
    case 'screen/set':
      // Leaving the profile drops the filter with it; coming back to a stale search that hid
      // everything reads as a broken page rather than as a filter.
      return action.screen === state.screen ? state : { ...state, screen: action.screen, search: '' }
    case 'dog/select':
      return state.selectedDogId === action.dogId
        ? state
        : { ...state, selectedDogId: action.dogId, tab: 'all', search: '', step: 0 }
    case 'tab/set':
      return { ...state, tab: action.tab }
    case 'search/set':
      return { ...state, search: action.search }
    case 'step/set':
      return { ...state, step: action.step }
    case 'reminder/toggle':
      return { ...state, toggles: { ...state.toggles, [action.key]: !state.toggles[action.key] } }
    case 'toast/show':
      return { ...state, toast: { message: action.message, visible: true } }
    case 'toast/hide':
      return { ...state, toast: { ...state.toast, visible: false } }
    case 'toast/clear':
      return { ...state, toast: { message: '', visible: false } }
  }
}

function clampStep(step: number): StepId {
  const bounded = Math.max(0, Math.min(STEP_COUNT - 1, Math.trunc(step)))
  // The clamp above already guarantees membership; the cast is the only way to say so to TS.
  return bounded as StepId
}

// ── The record ──────────────────────────────────────────────────────────────────────────────────

export interface MeResult {
  /**
   * `loading` — nothing to show yet.
   * `ready`   — `data` is present. A later refresh may still have failed, in which case `error`
   *             is set as well: showing the last good record with a "could not refresh" note is
   *             more honest than blanking a client's dog.
   * `error`   — the first load failed. `data` is null and MUST NOT be rendered as "no dogs".
   */
  readonly status: 'loading' | 'ready' | 'error'
  /** First load only. Never true while there is already something on screen. */
  readonly loading: boolean
  /** A reload in flight behind existing data. Use it for a quiet indicator, not a skeleton. */
  readonly refreshing: boolean
  readonly data: MePayload | null
  readonly error: ApiFailure | null
  /** HTTP status of the failure. 401 means the session expired — send them to sign in. */
  readonly errorStatus: number | null
  readonly refresh: () => void
}

interface MeInternal {
  readonly data: MePayload | null
  readonly error: ApiFailure | null
  readonly errorStatus: number | null
  readonly inFlight: boolean
}

const ME_INITIAL: MeInternal = { data: null, error: null, errorStatus: null, inFlight: true }

function useMeSource(
  /** Defaults to the signed-in client's own record. Staff pass a household loader — see `ViewingAs`. */
  loadRecord: (options: { signal: AbortSignal }) => Promise<ApiResult<MePayload>> = getMe
): {
  readonly me: MeResult
  readonly applyCare: (dogId: string, care: readonly CareItem[]) => void
  readonly applyFood: (dogId: string, food: FoodAvailability) => void
} {
  const [state, setState] = useState<MeInternal>(ME_INITIAL)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    let live = true

    setState((prev) => ({ ...prev, inFlight: true }))

    void loadRecord({ signal: controller.signal }).then((result) => {
      if (!live) return
      if (result.ok) {
        setState({ data: result.data, error: null, errorStatus: null, inFlight: false })
        return
      }
      // The caller cancelled — a StrictMode remount or an unmount, not a fault. Rendering it as
      // one would flash an error banner on every mount in development.
      if (result.error.code === 'aborted') return
      setState((prev) => ({
        data: prev.data,
        error: result.error,
        errorStatus: result.status,
        inFlight: false,
      }))
    })

    return () => {
      live = false
      controller.abort()
    }
    // `loadRecord` is deliberately NOT a dependency. Staff pass an inline closure over the
    // household id, which is a new function on every render — depending on it would refetch the
    // record in a loop. `nonce` is the only thing that should re-run this, and a change of
    // household remounts the provider with a fresh `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce])

  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  /**
   * Splice a recomputed schedule into the cached record after a "mark as given".
   *
   * The write endpoint returns the new schedule precisely so the client does not have to refetch
   * the household to see one date move. `careEmpty` is recomputed with it, or a dog whose very
   * first care item was just recorded would keep showing the "nothing recorded yet" card.
   */
  const applyCare = useCallback((dogId: string, care: readonly CareItem[]) => {
    setState((prev) => {
      if (!prev.data) return prev
      const dogs = prev.data.dogs.map((dog) =>
        dog.dogId === dogId ? { ...dog, care, careEmpty: care.length === 0 } : dog,
      )
      return { ...prev, data: { ...prev.data, dogs } }
    })
  }, [])

  /** Splice the server-derived food response into exactly the dog whose edit was saved. */
  const applyFood = useCallback((dogId: string, food: FoodAvailability) => {
    setState((prev) => {
      if (!prev.data) return prev
      const dogs = prev.data.dogs.map((dog) => dog.dogId === dogId ? { ...dog, food } : dog)
      return { ...prev, data: { ...prev.data, dogs } }
    })
  }, [])

  const me = useMemo<MeResult>(() => {
    const status: MeResult['status'] = state.data ? 'ready' : state.inFlight ? 'loading' : 'error'
    return {
      status,
      loading: status === 'loading',
      refreshing: state.inFlight && state.data !== null,
      data: state.data,
      error: state.error,
      errorStatus: state.errorStatus,
      refresh,
    }
  }, [state, refresh])

  return { me, applyCare, applyFood }
}

// ── Context ─────────────────────────────────────────────────────────────────────────────────────

export interface PortalContextValue {
  readonly state: PortalUiState
  /** Escape hatch for anything the named actions do not cover. Prefer the named actions. */
  readonly dispatch: Dispatch<PortalAction>
  readonly me: MeResult

  /** Navigate and return to the top of the page. */
  readonly go: (screen: ScreenId) => void
  /** Jump into a specific review step from elsewhere (a profile section's "Check" button). */
  readonly goStep: (step: StepId) => void
  readonly nextStep: () => void
  readonly prevStep: () => void
  readonly selectDog: (dogId: string) => void
  readonly setTab: (tab: TabId) => void
  readonly setSearch: (search: string) => void
  readonly clearSearch: () => void
  readonly toggleReminder: (key: ToggleKey) => void
  /** Announce something. Fades at 2400ms; the live region's text is cleared at 2750ms. */
  readonly showToast: (message: string) => void
  /** Replace only one dog's food with the server's recomputed, overlaid result. */
  readonly applyFood: (dogId: string, food: FoodAvailability) => void

  /**
   * The portal's only write. Resolves to the API result — it does not throw and it does not toast;
   * the calling screen owns the wording so it can name the treatment.
   *
   * On success the returned schedule is spliced into the cached record, so no refetch is needed.
   */
  readonly markGiven: (request: MarkGivenRequest) => Promise<ApiResult<MarkGivenResponse>>

  /** Null for a client looking at their own record — the normal case. See `ViewingAs`. */
  readonly viewingAs: ViewingAs | null
}

const PortalContext = createContext<PortalContextValue | null>(null)

const TOAST_FADE_MS = 2400
/** 350ms after the fade starts — long enough for the .25s CSS transition to finish. */
const TOAST_CLEAR_MS = 2750

/**
 * Who is looking, when it is not the client themselves.
 *
 * Staff troubleshoot by opening the REAL customer portal for a household — same screens, same
 * components, same everything — because a support tool that merely resembles the customer's view
 * cannot answer "what are you seeing?". This is the only concession the customer tree makes to
 * that: where the record comes from, and a way back out.
 *
 * Null in every client session, which is the normal case and the default.
 */
export interface ViewingAs {
  readonly staffEmail: string
  /** Whose record is open — shown permanently so it cannot be mistaken for the viewer's own. */
  readonly householdName: string | null
  readonly onExit: () => void
}

export interface PortalProviderProps {
  readonly children?: ReactNode
  /** Tests and stories only. Production always starts from `INITIAL_UI_STATE`. */
  readonly initialState?: PortalUiState
  /**
   * Where to load the record from. Defaults to `getMe` — the signed-in client's own record.
   *
   * Injected rather than branching inside `useMeSource`, so the staff path cannot change what a
   * client's own fetch does: on a customer's session this prop is absent and the code below is
   * byte-for-byte what it always was.
   */
  readonly loadRecord?: (options: { signal: AbortSignal }) => Promise<ApiResult<MePayload>>
  /** Set only when staff are viewing somebody else's record. */
  readonly viewingAs?: ViewingAs
}

export function PortalProvider({
  children,
  initialState,
  loadRecord,
  viewingAs,
}: PortalProviderProps): ReactElement {
  const [state, dispatch] = useReducer(portalReducer, initialState ?? INITIAL_UI_STATE)
  const { me, applyCare, applyFood } = useMeSource(loadRecord)

  // Timer handles, not state: bumping state on a fade would re-render the whole tree twice per
  // toast. `types: ["vite/client"]` keeps @types/node out, so these really are DOM timer numbers.
  const fadeTimer = useRef<number | null>(null)
  const clearTimer = useRef<number | null>(null)

  useEffect(() => {
    // The prototype's class had no unmount hook and leaked both timers. Clearing them here is the
    // difference between a clean unmount and a setState on a dead tree.
    return () => {
      if (fadeTimer.current !== null) window.clearTimeout(fadeTimer.current)
      if (clearTimer.current !== null) window.clearTimeout(clearTimer.current)
    }
  }, [])

  const showToast = useCallback((message: string) => {
    if (fadeTimer.current !== null) window.clearTimeout(fadeTimer.current)
    if (clearTimer.current !== null) window.clearTimeout(clearTimer.current)
    dispatch({ type: 'toast/show', message })
    fadeTimer.current = window.setTimeout(() => dispatch({ type: 'toast/hide' }), TOAST_FADE_MS)
    clearTimer.current = window.setTimeout(() => dispatch({ type: 'toast/clear' }), TOAST_CLEAR_MS)
  }, [])

  const go = useCallback((screen: ScreenId) => {
    dispatch({ type: 'screen/set', screen })
    scrollTop()
  }, [])

  const goStep = useCallback((step: StepId) => {
    dispatch({ type: 'screen/set', screen: 'review' })
    dispatch({ type: 'step/set', step })
    scrollTop()
  }, [])

  /**
   * Both of these compute the target from the step CAPTURED AT RENDER, not from a functional
   * updater — and that is the safe choice here, not the sloppy one. Two clicks landing in the same
   * frame both compute the same target, so the wizard advances once. The prototype used a
   * functional increment against a render-captured guard, which let a double-click push `step` to
   * 6: a blank title, a blank question, "Step 7 of 6", and all six progress bars painted complete.
   * `clampStep` makes that unrepresentable regardless.
   */
  const nextStep = useCallback(() => {
    const next = clampStep(state.step + 1)
    if (next !== state.step) {
      dispatch({ type: 'step/set', step: next })
      scrollTop()
    }
  }, [state.step])

  const prevStep = useCallback(() => {
    // No scrollTop: the reader is already looking at the part of the page they are going back to.
    const previous = clampStep(state.step - 1)
    if (previous !== state.step) dispatch({ type: 'step/set', step: previous })
  }, [state.step])

  const selectDog = useCallback((dogId: string) => dispatch({ type: 'dog/select', dogId }), [])
  const setTab = useCallback((tab: TabId) => dispatch({ type: 'tab/set', tab }), [])
  const setSearch = useCallback((search: string) => dispatch({ type: 'search/set', search }), [])
  const clearSearch = useCallback(() => {
    dispatch({ type: 'search/set', search: '' })
    dispatch({ type: 'tab/set', tab: 'all' })
  }, [])
  const toggleReminder = useCallback((key: ToggleKey) => dispatch({ type: 'reminder/toggle', key }), [])

  const markGiven = useCallback(
    async (request: MarkGivenRequest): Promise<ApiResult<MarkGivenResponse>> => {
      const result = await postMarkGiven(request)
      if (result.ok) applyCare(request.dogId, result.data.care)
      return result
    },
    [applyCare],
  )

  const value = useMemo<PortalContextValue>(
    () => ({
      state,
      dispatch,
      me,
      go,
      goStep,
      nextStep,
      prevStep,
      selectDog,
      setTab,
      setSearch,
      clearSearch,
      toggleReminder,
      showToast,
      applyFood,
      markGiven,
      viewingAs: viewingAs ?? null,
    }),
    [
      viewingAs,
      state,
      me,
      go,
      goStep,
      nextStep,
      prevStep,
      selectDog,
      setTab,
      setSearch,
      clearSearch,
      toggleReminder,
      showToast,
      applyFood,
      markGiven,
    ],
  )

  return <PortalContext.Provider value={value}>{children}</PortalContext.Provider>
}

export function usePortal(): PortalContextValue {
  const value = useContext(PortalContext)
  if (!value) throw new Error('usePortal() must be called inside <PortalProvider>')
  return value
}

/** The household record. One request, one copy, shared by every screen. */
export function useMe(): MeResult {
  return usePortal().me
}

/**
 * The dog currently being looked at.
 *
 * Falls back to the first dog when nothing is selected, and also when the selected id no longer
 * matches anything — a stale id after a refresh must not render an empty screen. Returns null only
 * when there genuinely is no dog, which is a real state a household can be in and which the screen
 * has to say out loud rather than render as a blank.
 */
export function useSelectedDog(): DogPayload | null {
  const { state, me } = usePortal()
  const dogs = me.data?.dogs
  return useMemo(() => {
    if (!dogs || dogs.length === 0) return null
    const chosen = state.selectedDogId === null ? undefined : dogs.find((d) => d.dogId === state.selectedDogId)
    return chosen ?? dogs[0] ?? null
  }, [dogs, state.selectedDogId])
}
